import React, { useEffect, useState } from 'react';
import CommonLayout from '../../../layouts/CommonLayout';
import { Container, Spinner } from 'react-bootstrap';
import { allAPiServicesCall } from '../../../services/apiServices';
import { getAuthConfig } from '../../../services/apiUtils';
import {
    FaDatabase,
    FaNewspaper,
    FaMusic,
    FaTrash,
    FaSync,
    FaCheckCircle,
    FaTimesCircle,
    FaServer,
    FaSearch,
    FaBook,
    FaUserAlt,
    FaMicrophone,
    FaCompactDisc,
    FaUser,
    FaYoutube,
    FaCode,
    FaImage,
    FaListAlt,
    FaHeart,
    FaPodcast,
    FaQuestionCircle,
    FaPlay,
    FaChevronDown,
    FaChevronUp
} from 'react-icons/fa';
import './CacheManagement.css';

// Cache key categories data
const cacheKeyCategories = [
    {
        id: 'songs',
        title: 'Songs',
        icon: FaPlay,
        keys: ['get-top-songs', 'get-new-release-songs', 'get-most-played-songs', 'get-most-popular-songs-list', 'get-recommended-songs-list', 'get-explore-song-list', 'get-album-songs', 'search-song', 'song-query-search', 'get-related-content', 'admin-list-songs']
    },
    {
        id: 'artists',
        title: 'Artists',
        icon: FaMicrophone,
        keys: ['get-all-artist', 'get-trending-artists', 'get-recommended-artists', 'get-top-artists', 'get-artist-bio-with-top', 'get-artists-by-letter', 'get-search-result', 'artist-biblio']
    },
    {
        id: 'albums',
        title: 'Albums',
        icon: FaCompactDisc,
        keys: ['get-top-explore-albums', 'get-explore-albums', 'get-artist-albums']
    },
    {
        id: 'playlists',
        title: 'Playlists',
        icon: FaListAlt,
        keys: ['get-all-admin-playlist', 'explore-playlists', 'get-playlist-songs', 'user-playlist', 'get-user-playlist-songs', 'get-all-user-playlist']
    },
    {
        id: 'news',
        title: 'News',
        icon: FaNewspaper,
        keys: ['get-news-list', 'get-news']
    },
    {
        id: 'userData',
        title: 'User Data',
        icon: FaUser,
        keys: ['user-profile', 'get-favourite-artist', 'all-favourite-songs', 'user-comments']
    },
    {
        id: 'other',
        title: 'Other Content',
        icon: FaImage,
        keys: ['get-banner', 'get-genres', 'get-testimonial', 'get-all-youtube-videos']
    },
    {
        id: 'jsonld',
        title: 'JSON-LD Schema',
        icon: FaCode,
        keys: ['album-json-ld', 'artist-json-ld', 'playlist-json-ld', 'news-json-ld', 'podcast-json-ld', 'video-json-ld', 'quiz-json-ld', 'qa-json-ld']
    }
];

const CacheManagement = () => {
    const [loading, setLoading] = useState(false);
    const [loadingAction, setLoadingAction] = useState(null);
    const [redisStatus, setRedisStatus] = useState(null);
    const [customPattern, setCustomPattern] = useState('');
    const [lastCleared, setLastCleared] = useState({});
    const [expandedSections, setExpandedSections] = useState({});

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    const checkRedisStatus = async () => {
        try {
            const response = await allAPiServicesCall.pingRedis({}, getAuthConfig());
            if (response) {
                setRedisStatus({ connected: true, pong: response.data?.pong });
            } else {
                setRedisStatus({ connected: false });
            }
        } catch (error) {
            setRedisStatus({ connected: false, error: error.message });
        }
    };

    useEffect(() => {
        checkRedisStatus();
    }, []);

    const handleClearAllCache = async () => {
        setLoadingAction('all');
        try {
            const response = await allAPiServicesCall.clearAllCache({}, getAuthConfig());
            if (response) {
                setLastCleared({ ...lastCleared, all: { time: new Date(), count: response.data?.deletedCount } });
            }
        } finally {
            setLoadingAction(null);
        }
    };

    const handleClearNewsCache = async () => {
        setLoadingAction('news');
        try {
            const response = await allAPiServicesCall.clearNewsCache({}, getAuthConfig());
            if (response) {
                setLastCleared({ ...lastCleared, news: { time: new Date(), count: response.data?.deletedCount } });
            }
        } finally {
            setLoadingAction(null);
        }
    };

    const handleClearPlaylistsCache = async () => {
        setLoadingAction('playlists');
        try {
            const response = await allAPiServicesCall.clearPlaylistsCache({}, getAuthConfig());
            if (response) {
                setLastCleared({ ...lastCleared, playlists: { time: new Date(), count: response.data?.deletedCount } });
            }
        } finally {
            setLoadingAction(null);
        }
    };

    const handleClearByPattern = async () => {
        if (!customPattern.trim()) return;
        setLoadingAction('pattern');
        try {
            const response = await allAPiServicesCall.clearCacheByPattern(customPattern, getAuthConfig());
            if (response) {
                setLastCleared({ ...lastCleared, pattern: { time: new Date(), count: response.data?.deletedCount, pattern: customPattern } });
                setCustomPattern('');
            }
        } finally {
            setLoadingAction(null);
        }
    };

    const cacheOptions = [
        {
            id: 'all',
            title: 'Clear All Cache',
            description: 'Clears all cached data from Redis. Use this when you want a complete cache refresh.',
            icon: <FaDatabase />,
            action: handleClearAllCache,
        },
        {
            id: 'news',
            title: 'Clear News Cache',
            description: 'Clears cache for news articles, news list, and news JSON-LD. Use after updating news.',
            icon: <FaNewspaper />,
            action: handleClearNewsCache,
        },
        {
            id: 'playlists',
            title: 'Clear Playlists Cache',
            description: 'Clears cache for playlists, playlist songs, and explore playlists.',
            icon: <FaMusic />,
            action: handleClearPlaylistsCache,
        },
    ];

    return (
        <CommonLayout>
            <div className="cache-management-page">
                <Container className="cache-management-container mt-5 pt-5">
                    {/* Premium Header */}
                    <div className="cache-management-header">
                        <div className="cache-management-header-content">
                            <div className="cache-management-header-left">
                                <div className="cache-management-header-icon">
                                    <FaServer />
                                </div>
                                <div className="cache-management-header-text">
                                    <h1>Cache Management</h1>
                                    <p>Manage and clear Redis cache for optimal performance</p>
                                </div>
                            </div>
                            <div className="cache-management-header-right">
                                {redisStatus && (
                                    <div className={`redis-status-badge ${redisStatus.connected ? 'connected' : 'disconnected'}`}>
                                        <span className={`redis-status-dot ${redisStatus.connected ? 'connected' : 'disconnected'}`}></span>
                                        {redisStatus.connected ? <FaCheckCircle /> : <FaTimesCircle />}
                                        Redis: {redisStatus.connected ? 'Connected' : 'Disconnected'}
                                    </div>
                                )}
                                <button className="refresh-status-btn" onClick={checkRedisStatus}>
                                    <FaSync />
                                    Refresh Status
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Cache Action Cards */}
                    <div className="cache-cards-grid">
                        {cacheOptions.map((option) => (
                            <div key={option.id} className="cache-action-card">
                                <div className={`cache-card-icon ${option.id}`}>
                                    {option.icon}
                                </div>
                                <h3 className="cache-card-title">{option.title}</h3>
                                <p className="cache-card-description">{option.description}</p>

                                {lastCleared[option.id] && (
                                    <div className="cache-card-cleared">
                                        <FaCheckCircle />
                                        <span className="cache-card-cleared-text">
                                            Last cleared: {lastCleared[option.id].time.toLocaleTimeString()}
                                            {lastCleared[option.id].count !== undefined && (
                                                <> ({lastCleared[option.id].count} entries)</>
                                            )}
                                        </span>
                                    </div>
                                )}

                                <button
                                    className={`cache-card-btn ${option.id}`}
                                    onClick={option.action}
                                    disabled={loadingAction !== null}
                                >
                                    {loadingAction === option.id ? (
                                        <>
                                            <Spinner animation="border" size="sm" />
                                            Clearing...
                                        </>
                                    ) : (
                                        <>
                                            <FaTrash />
                                            Clear Cache
                                        </>
                                    )}
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Pattern Clear Section */}
                    <div className="pattern-clear-card">
                        <div className="pattern-clear-header">
                            <div className="pattern-clear-icon">
                                <FaSearch />
                            </div>
                            <div>
                                <h3 className="pattern-clear-title">Clear Cache by Pattern</h3>
                                <p className="pattern-clear-subtitle">Advanced: Clear cache entries matching a specific pattern. Use wildcards (*) for partial matches.</p>
                            </div>
                        </div>

                        <div className="pattern-input-group">
                            <div className="pattern-input-wrapper">
                                <input
                                    type="text"
                                    className="pattern-input"
                                    placeholder="e.g., get-news-list* or album-json-ld*"
                                    value={customPattern}
                                    onChange={(e) => setCustomPattern(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && customPattern.trim()) {
                                            handleClearByPattern();
                                        }
                                    }}
                                />
                                <p className="pattern-hint">
                                    Common patterns: <code>get-news-list*</code> <code>get-news*</code> <code>news-json-ld*</code> <code>explore-playlists*</code> <code>get-playlist-songs*</code>
                                </p>
                            </div>
                            <button
                                className="pattern-clear-btn"
                                onClick={handleClearByPattern}
                                disabled={loadingAction !== null || !customPattern.trim()}
                            >
                                {loadingAction === 'pattern' ? (
                                    <>
                                        <Spinner animation="border" size="sm" />
                                        Clearing...
                                    </>
                                ) : (
                                    <>
                                        <FaTrash />
                                        Clear by Pattern
                                    </>
                                )}
                            </button>
                        </div>

                        {lastCleared.pattern && (
                            <div className="pattern-cleared-info">
                                <FaCheckCircle />
                                <span>
                                    Last cleared pattern "{lastCleared.pattern.pattern}": {lastCleared.pattern.time.toLocaleTimeString()}
                                    {lastCleared.pattern.count !== undefined && (
                                        <> ({lastCleared.pattern.count} entries)</>
                                    )}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Cache Key Reference */}
                    <div className="cache-reference-card">
                        <div className="cache-reference-header">
                            <div className="cache-reference-icon">
                                <FaBook />
                            </div>
                            <div>
                                <h3 className="cache-reference-title">Cache Key Reference</h3>
                                <p className="cache-reference-subtitle">Complete list of all cache keys used in the application (47+ keys)</p>
                            </div>
                        </div>

                        <div className="cache-reference-grid-full">
                            {cacheKeyCategories.map((category) => {
                                const IconComponent = category.icon;
                                const isExpanded = expandedSections[category.id];
                                const visibleKeys = isExpanded ? category.keys : category.keys.slice(0, 4);
                                const hasMore = category.keys.length > 4;

                                return (
                                    <div key={category.id} className="cache-reference-section">
                                        <h4 className="cache-reference-section-title">
                                            <IconComponent />
                                            {category.title}
                                            <span className="cache-key-count">{category.keys.length}</span>
                                        </h4>
                                        <ul className="cache-key-list">
                                            {visibleKeys.map((key) => (
                                                <li key={key}><span className="cache-key-code">{key}</span></li>
                                            ))}
                                        </ul>
                                        {hasMore && (
                                            <button
                                                className="cache-see-more-btn"
                                                onClick={() => toggleSection(category.id)}
                                            >
                                                {isExpanded ? (
                                                    <>
                                                        <FaChevronUp />
                                                        Show less
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaChevronDown />
                                                        +{category.keys.length - 4} more
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="cache-reference-footer">
                            <p><strong>Tip:</strong> Use wildcards (*) to clear multiple related keys. Example: <code>get-news*</code> clears all news-related cache.</p>
                        </div>
                    </div>
                </Container>
            </div>
        </CommonLayout>
    );
};

export default CacheManagement;
